// Pure mob definitions, AI decisions, and drop tables. No Pixi, no DOM. (TECHNICAL_DESIGN.md §2, §9.)
import type { Rng } from './rng';
import type { Biome, ItemId } from './types';

export type MobState = 'wander' | 'chase' | 'windup' | 'attack';

export interface MobDef {
  id: string;
  name: string;
  hp: number;
  dmg: number;
  speed: number; // world px/s
  aggroRadius: number; // only used by hostile mobs, or provoked neutrals
  xp: number;
  neutral: boolean; // cozy rule: wildlife true (fights only if provoked)
  packLink: boolean; // hitting one provokes nearby kin (wolves)
  nightOnly: boolean;
  drops: { item: ItemId | 'gold'; min: number; max: number; chance: number }[];
}

export const MOBS: Record<string, MobDef> = {
  slime: {
    id: 'slime', name: 'Slime', hp: 15, dmg: 4, speed: 55, aggroRadius: 120, xp: 3,
    neutral: true, packLink: false, nightOnly: false,
    drops: [{ item: 'gold', min: 1, max: 3, chance: 1 }],
  },
  wolf: {
    id: 'wolf', name: 'Wolf', hp: 30, dmg: 8, speed: 110, aggroRadius: 170, xp: 6,
    neutral: true, packLink: true, nightOnly: false,
    drops: [
      { item: 'hide', min: 1, max: 2, chance: 1 },
      { item: 'raw_meat', min: 1, max: 1, chance: 0.7 },
      { item: 'gold', min: 1, max: 4, chance: 0.8 },
    ],
  },
  skeleton: {
    id: 'skeleton', name: 'Skeleton', hp: 45, dmg: 12, speed: 85, aggroRadius: 200, xp: 10,
    neutral: false, packLink: false, nightOnly: true,
    drops: [{ item: 'gold', min: 2, max: 6, chance: 1 }],
  },
  // boar — day wildlife, sturdier than a wolf, good hide/meat
  boar: {
    id: 'boar', name: 'Boar', hp: 50, dmg: 10, speed: 95, aggroRadius: 150, xp: 8,
    neutral: true, packLink: false, nightOnly: false,
    drops: [
      { item: 'hide', min: 2, max: 3, chance: 1 },
      { item: 'raw_meat', min: 1, max: 2, chance: 0.9 },
      { item: 'gold', min: 2, max: 5, chance: 0.6 },
    ],
  },
  // direwolf — a NIGHT pack hunter; hostile on sight, fast, hits hard
  direwolf: {
    id: 'direwolf', name: 'Direwolf', hp: 60, dmg: 15, speed: 128, aggroRadius: 240, xp: 16,
    neutral: false, packLink: true, nightOnly: true,
    drops: [
      { item: 'hide', min: 2, max: 3, chance: 1 },
      { item: 'raw_meat', min: 1, max: 2, chance: 0.8 },
      { item: 'gold', min: 5, max: 10, chance: 1 },
    ],
  },
  // wraith — a fast, fragile NIGHT/corruption horror
  wraith: {
    id: 'wraith', name: 'Wraith', hp: 32, dmg: 16, speed: 140, aggroRadius: 260, xp: 14,
    neutral: false, packLink: false, nightOnly: true,
    drops: [{ item: 'gold', min: 6, max: 12, chance: 1 }],
  },
  // wisp — a RARE, harmless drifting light at night; slay it for a purse of gold
  wisp: {
    id: 'wisp', name: 'Wisp', hp: 18, dmg: 0, speed: 45, aggroRadius: 0, xp: 12,
    neutral: true, packLink: false, nightOnly: true,
    drops: [{ item: 'gold', min: 15, max: 30, chance: 1 }],
  },
  // golden slime — a RARE daytime treasure; slow, harmless, a jackpot of gold
  golden_slime: {
    id: 'golden_slime', name: 'Golden Slime', hp: 26, dmg: 4, speed: 50, aggroRadius: 90, xp: 15,
    neutral: true, packLink: false, nightOnly: false,
    drops: [{ item: 'gold', min: 20, max: 40, chance: 1 }],
  },
  // dungeon boss (every 3rd floor) — a big, hostile, slow-telegraph elite
  bone_warden: {
    id: 'bone_warden', name: 'Bone Warden', hp: 160, dmg: 20, speed: 70, aggroRadius: 400, xp: 40,
    neutral: false, packLink: false, nightOnly: false,
    drops: [{ item: 'gold', min: 20, max: 40, chance: 1 }],
  },
};

/** Which mob (if any) may spawn for a biome + time-of-day. Returns a def id or null. */
export function spawnCandidate(biome: Biome, isNight: boolean, nearCorruption: boolean, rng: Rng): string | null {
  if (nearCorruption || (isNight && rng.float() < 0.6)) {
    // corrupted ground / night favours the undead
    const r = rng.float();
    if (nearCorruption && r < 0.08) return 'wraith'; // a rare corrupted horror
    if (r < 0.7) return 'skeleton';
  }
  // a rare drifting wisp — a treasure worth hunting in the dark
  if (isNight && rng.float() < 0.05) return 'wisp';
  if (biome === 'meadow') {
    if (rng.float() < 0.03) return 'golden_slime'; // rare daytime jackpot
    return rng.float() < 0.6 ? 'slime' : null;
  }
  if (biome === 'forest') {
    if (isNight && rng.float() < 0.28) return 'direwolf'; // night pack hunter
    if (rng.float() < 0.15) return 'boar';
    return rng.float() < 0.5 ? 'wolf' : null;
  }
  if (biome === 'highlands') {
    if (rng.float() < 0.12) return 'boar';
    return rng.float() < 0.3 ? 'slime' : null;
  }
  return null;
}

/** Pure AI transition. `provoked` covers neutral mobs the player has hit. */
export function nextState(args: {
  def: MobDef;
  distToPlayer: number;
  provoked: boolean;
  attackCdReady: boolean;
  windupDone: boolean;
}): MobState {
  const { def, distToPlayer, provoked, attackCdReady, windupDone } = args;
  const hostile = !def.neutral || provoked;
  if (!hostile) return 'wander';
  if (distToPlayer > def.aggroRadius * 1.4) return 'wander'; // lost interest
  if (distToPlayer <= 26) {
    if (windupDone && attackCdReady) return 'attack';
    return 'windup';
  }
  return 'chase';
}

/** Roll a mob's drops. Uses an injected rng (spawn/loot stream). */
export function rollDrops(def: MobDef, rng: Rng): { item: ItemId | 'gold'; n: number }[] {
  const out: { item: ItemId | 'gold'; n: number }[] = [];
  for (const d of def.drops) {
    if (rng.float() <= d.chance) {
      const n = d.min + Math.floor(rng.float() * (d.max - d.min + 1));
      if (n > 0) out.push({ item: d.item, n });
    }
  }
  return out;
}
